import { v4 as uuidv4 } from 'uuid';
import type { SimulationRequestInterface } from './types';
import type {
  ConnectionInterface,
  SimulationRequestConnectionInterface,
} from 'store/connections/types';
import type {
  SimulationRequestUnitInterface,
  UnitInterface,
  UnitInfoInterface,
  SimulationRequestPortsInterface,
  PortsMapInterface,
  FlowInterface,
} from 'store/units/types';
import { PortMode } from 'store/units/types';
import { screenUnitTypes, UnitTypes } from 'unitData/types';
import {
  getPortLimits,
  getScreenOutputPortCount,
} from 'store/units/unitRegistry';
import {
  getMobileWheeledConeRequest,
  getMobileJawRequest,
  mobileTrackedConeRequest,
  getMobileTrackedScreenRequest,
  getMobileWheeledScreen,
  getMobileHSIRequest,
  getMobileVSIRequest,
  getMobileJawAndConeRequest,
} from './unitRequests/mobile';
import {
  bananaScreenRequest,
  cmScreenRequest,
  coneCrusherCHRequest,
  coneCrusherCSRequest,
  feedRequest,
  feederAndScreenRequest,
  feederRequest,
  freefallSFScreenRequest,
  freefallSSScreenRequest,
  genericCrusherRequest,
  genericScreenRequest,
  getBreakerRequest,
  grizzlyFeederRequest,
  grizzlyScreenRequest,
  gyratoryCrusherRequest,
  hybridCrusherRequest,
  impactCrusherCIRequest,
  impactCrusherCVRequest,
  jawCrusherRequest,
  kwataniScreenRequest,
  linearMotionScreenRequest,
  multislopeScreenRequest,
  schenckScreenRequest,
  storageFeederRequest,
  storageFeederTwoOutputsRequest,
  storageRequest,
} from './unitRequests/stationary';

export const cleanUpRequestParams = (
  simulationParams: SimulationRequestInterface,
) => {
  const requestPorts: SimulationRequestPortsInterface = getRequestPorts(
    simulationParams.portMapping,
    simulationParams.units,
  );

  const requestUnits: SimulationRequestUnitInterface[] = getRequestUnits(
    simulationParams.units,
    simulationParams.globalConfig.screenCalcMode,
    simulationParams.globalConfig.applicationType,
  );

  const validPortKeys = new Set(
    Object.values(requestPorts).flatMap((ports) => ports.map((p) => p.key)),
  );

  const requestConnections: SimulationRequestConnectionInterface[] =
    getRequestConnections(simulationParams.connections, validPortKeys);
  return {
    units: requestUnits,
    portMapping: requestPorts,
    connections: requestConnections,
    globalConfig: simulationParams.globalConfig,
  };
};

const getRequestConnections = (
  connections: ConnectionInterface[],
  validPortKeys: Set<string>,
) => {
  return connections
    .filter((c) => validPortKeys.has(c.fromPort) && validPortKeys.has(c.toPort))
    .map((connection: ConnectionInterface) => {
      const reqConnection: SimulationRequestConnectionInterface = {
        key: connection.key,
        from: connection.fromPort,
        to: connection.toPort,
        conveyorCalc: connection.conveyorCalc,
        connectionInfo: connection.connectionInfo,
      };
      return reqConnection;
    });
};

export const getRequestPorts = (
  ports: PortsMapInterface,
  units: UnitInterface[],
) => {
  const cleanPortsMap: PortsMapInterface = {};
  for (const [key, value] of Object.entries(ports)) {
    const unit = units.find((u) => u.key === key);

    if (!unit?.layout) {
      continue;
    }

    // Include ports actually used by the unit. Screens have one output for
    // every deck plus a final oversize output. Derive this from unit info so
    // old saves with stale layout.outputPorts values still simulate correctly.
    const nbrOfInputPorts = unit.layout.inputPorts;
    const category = unit.layout.category as UnitTypes;
    const isScreen =
      screenUnitTypes.includes(category) ||
      category === UnitTypes.GENERIC_SCREEN;
    const isGrizzlyFeeder =
      category === UnitTypes.GRIZZLY_FEEDER_SW ||
      category === UnitTypes.GRIZZLY_FEEDER_SV ||
      category === UnitTypes.GRIZZLY_FEEDER_SV_E;
    const { maxOutputPorts } = getPortLimits(category);
    const nbrOfOutputPorts =
      isScreen &&
      'nbrOfDecks' in unit.unitInfo &&
      typeof unit.unitInfo.nbrOfDecks === 'number'
        ? getScreenOutputPortCount(unit.unitInfo.nbrOfDecks)
        : isGrizzlyFeeder
          ? Math.max(unit.layout.outputPorts, maxOutputPorts)
          : unit.layout.outputPorts;
    const inputPorts = value.filter(
      (p) => p.portMode === PortMode.INPUT && p.portIndex <= nbrOfInputPorts,
    );
    const outputPorts = value.filter(
      (p) => p.portMode === PortMode.OUTPUT && p.portIndex <= nbrOfOutputPorts,
    );
    const cleanPorts = [...inputPorts, ...outputPorts].map((port) => {
      return {
        key: port.key,
        unitKey: port.unitKey,
        portMode: port.portMode,
        portIndex: port.portIndex,
      };
    });

    // Synthesize any missing output port indices so the simulation backend
    // receives the full set it expects. This also handles saves that only
    // stored as many output port objects as the screen has decks.
    const presentOutputIndices = new Set(
      cleanPorts
        .filter((p) => p.portMode === PortMode.OUTPUT)
        .map((p) => p.portIndex),
    );
    for (let i = 1; i <= nbrOfOutputPorts; i++) {
      if (!presentOutputIndices.has(i)) {
        cleanPorts.push({
          key: uuidv4(),
          unitKey: key,
          portMode: PortMode.OUTPUT,
          portIndex: i,
        });
      }
    }

    cleanPortsMap[key] = cleanPorts;
  }
  return cleanPortsMap;
};

const noop = (unitInfo: UnitInfoInterface) => ({ unitInfo });

type UnitRequestFn = (
  unitInfo: UnitInfoInterface,
  screenCalcMethod: string,
  applicationType?: string,
) => Record<string, unknown>;

const FnMap = {
  [UnitTypes.CONE_CRUSHER_CH]: coneCrusherCHRequest,
  [UnitTypes.CONE_CRUSHER_CS]: coneCrusherCSRequest,
  [UnitTypes.JAW_CRUSHER_CJ]: jawCrusherRequest,
  [UnitTypes.HYBRID_CRUSHER_CR]: hybridCrusherRequest,
  [UnitTypes.GYRATORY_CRUSHER_CG]: gyratoryCrusherRequest,
  [UnitTypes.IMPACT_CRUSHER_CV]: impactCrusherCVRequest,
  [UnitTypes.IMPACT_CRUSHER_CI]: impactCrusherCIRequest,
  [UnitTypes.STORAGE_FEEDER]: storageFeederRequest,
  [UnitTypes.GENERIC_CRUSHER]: genericCrusherRequest,
  [UnitTypes.BANANA_SCREEN]: bananaScreenRequest,
  [UnitTypes.GRIZZLY_SCREEN]: grizzlyScreenRequest,
  [UnitTypes.FREEFALL_SCREEN_SF]: freefallSFScreenRequest,
  [UnitTypes.FREEFALL_SCREEN_SS]: freefallSSScreenRequest,
  [UnitTypes.FEEDER_AND_SCREEN]: feederAndScreenRequest,
  [UnitTypes.GENERIC_SCREEN]: genericScreenRequest,
  [UnitTypes.SCHENCK_SCREEN]: schenckScreenRequest,
  [UnitTypes.KWATANI_SCREEN]: kwataniScreenRequest,
  [UnitTypes.CONVENTIONAL_SCREEN]: cmScreenRequest,
  [UnitTypes.LINEAR_MOTION_SCREEN]: linearMotionScreenRequest,
  [UnitTypes.MULTISLOPE_SCREEN]: multislopeScreenRequest,
  [UnitTypes.STORAGE]: storageRequest,
  [UnitTypes.STORAGE_FEEDER_2_OUTPUTS]: storageFeederTwoOutputsRequest,
  [UnitTypes.MOBILE_TRACKED_CONE]: mobileTrackedConeRequest,
  [UnitTypes.MOBILE_WHEELED_CONE]: getMobileWheeledConeRequest,
  [UnitTypes.MOBILE_WHEELED_JAW]: getMobileJawRequest,
  [UnitTypes.MOBILE_TRACKED_JAW]: getMobileJawRequest,
  [UnitTypes.MOBILE_TRACKED_SCREEN]: getMobileTrackedScreenRequest,
  [UnitTypes.MOBILE_WHEELED_SCREEN]: getMobileWheeledScreen,
  [UnitTypes.GRIZZLY_FEEDER_SV_E]: grizzlyFeederRequest,
  [UnitTypes.GRIZZLY_FEEDER_SV]: grizzlyFeederRequest,
  [UnitTypes.GRIZZLY_FEEDER_SW]: grizzlyFeederRequest,
  [UnitTypes.PAN_FEEDER_SP]: feederRequest,
  [UnitTypes.PLATE_FEEDER_SH]: feederRequest,
  [UnitTypes.MOBILE_TRACKED_HSI]: getMobileHSIRequest,
  [UnitTypes.MOBILE_WHEELED_HSI]: getMobileHSIRequest,
  [UnitTypes.MOBILE_VSI]: getMobileVSIRequest,
  [UnitTypes.MOBILE_JAW_AND_CONE]: getMobileJawAndConeRequest,
  [UnitTypes.BREAKER]: getBreakerRequest,
  [UnitTypes.FEED]: feedRequest,

  [UnitTypes.ADD]: noop,
  [UnitTypes.SPLIT]: noop,
  [UnitTypes.FREE_TEXT_BOX]: noop,
  [UnitTypes.GRIZZLY_FEEDER_GENERAL]: noop,
  [UnitTypes.MOBILE_JAW_COMMON]: noop,
  [UnitTypes.MOBILE_CONE_COMMON]: noop,
  [UnitTypes.MOBILE_SCREEN_COMMON]: noop,
  [UnitTypes.MOBILE_HSI_COMMON]: noop,
  [UnitTypes.PRINT_WINDOW]: noop,
  [UnitTypes.HIGHLIGHT_TEXT_BOX]: noop,
} as unknown as Record<UnitTypes, UnitRequestFn>;

export const getRequestUnits = (
  units: UnitInterface[],
  screenCalcMethod: string,
  applicationType?: string,
): SimulationRequestUnitInterface[] => {
  const exludedUnits = [
    UnitTypes.HIGHLIGHT_TEXT_BOX,
    UnitTypes.PRINT_WINDOW,
    UnitTypes.FREE_TEXT_BOX,
  ];
  const getInputFlow = (unit: UnitInterface) => {
    if (!('inputFlow' in unit.unitInfo)) return undefined;
    const inputFlow: FlowInterface = {
      ...(unit.unitInfo.inputFlow as FlowInterface),
    };
    delete inputFlow.materialId;
    delete inputFlow.psdId;
    return inputFlow;
  };
  return units
    .filter((u) => !exludedUnits.includes(u.type))
    .map((unit) => {
      return {
        key: unit.key,
        type: unit.type,
        inputFlow: getInputFlow(unit),
        ...FnMap[unit.type](unit.unitInfo, screenCalcMethod, applicationType),
      };
    });
};
